import { Duplex } from 'readable-stream'
import pump from 'pump'
import browser from 'webextension-polyfill'

import { InternalAPI, InternalMethods } from '@/shared/apis/messages'
import { CONTROLLER, LOCAL_CHANNEL } from '@/shared/config'
import { setupMultiplex } from '@/shared/setupMultiplex'
import { PromiseManager } from '@/shared/promiseManager'
import { useAppStore } from '@/popup/store/AppStore'
import router from '@/popup/router/router'
import getUniqueId from '@/shared/getUniqueId'
import { logError, logInfo } from '@/shared/logger'
import { PortDuplexStream } from '@/shared/PortDuplexStream'
import { useApprovalsStore } from '@/popup/store/ApprovalsStore'
import { EthProvider } from '@/popup/utils/ethProvider'
import {
  BGNotificationAPI,
  BGNotifications,
} from '@/shared/apis/bgnotifications'
import { useGlobals } from '@/popup/compositions/useGlobals'
import { setup } from '@/popup/main'
import { Approval, ApprovalApi } from '@/shared/apis/approvals'
import { Transaction } from '@/shared/models/Transaction'
import { Sign } from '@/shared/models/Sign'
import { useProfileStore } from './store/ProfileStore'
import { useProviderStore } from './store/ProviderStore'

let promiseManager: PromiseManager
const processNotification = async <T extends keyof BGNotificationAPI>(data: {
  method: T
  payload: BGNotificationAPI[T]['payload']
}) => {
  const { sendMessage } = useGlobals()

  const appStore = useAppStore()
  const approvalsStore = useApprovalsStore()
  const profileStore = useProfileStore()
  const providerStore = useProviderStore()

  const notificationAPI: {
    [K in keyof BGNotificationAPI]: (
      payload: BGNotificationAPI[T]['payload']
    ) => Promise<void>
  } = {
    [BGNotifications.KEYCHAIN_LOCKED]: async () => {
      router.go(0)
    },
    [BGNotifications.ApprovalCancelEvent]: async (
      payload: BGNotificationAPI[T]['payload']
    ) => {
      logInfo('ApprovalCancelEvent')
      const approval = payload as Approval<keyof ApprovalApi, Transaction>
      approvalsStore.handleApprovalCancelled(approval.id)
    },
    [BGNotifications.RemoteProviderConnectionClosedEvent]: async () => {
      providerStore.handleConnectionClosed()
    },
    [BGNotifications.RemoteProviderConnectionExpirationEvent]: async () => {
      providerStore.handleConnectionExpired({
        id: getUniqueId(),
        type: 'RemoteProviderConnectionExpirationEvent',
      })
    },
    [BGNotifications.RemoteProviderConnectionEstablishedEvent]: async () => {
      logInfo('RemoteProviderConnectionEstablishedEvent')
      providerStore.handleConnectionEstablished({
        id: getUniqueId(),
        type: 'RemoteProviderConnectionEstablishedEvent',
      })
    },
    [BGNotifications.NEW_ACTIVITY]: async (
      payload: BGNotificationAPI[T]['payload']
      // eslint-disable-next-line sonarjs/cognitive-complexity
    ) => {
      const approval =
        payload as BGNotificationAPI[typeof BGNotifications.NEW_ACTIVITY]['payload']
      const approvalHandlers: {
        [K in keyof ApprovalApi]: () => void
      } = {
        transaction: async () => {
          const txApproval = approval as Approval<
            keyof ApprovalApi,
            Transaction
          >
          approvalsStore.addNonStashedApproval(txApproval)

          if (txApproval.requestData.from === appStore.selectedProfile) {
            approvalsStore.addProfileApproval(txApproval)
          } else {
            const data = await sendMessage({
              method: InternalMethods.SWITCH_PROFILE,
              payload: {
                address: txApproval.requestData.from,
              },
            })
            logInfo('streamSetup.processNotification transaction', data)
            await appStore.init(data, appStore.lastPopupState, false)
            approvalsStore.setProfileApprovals(data.approvals)
          }

          if (
            !router.currentRoute.value.name
              ?.toString()
              .startsWith('/transaction')
          ) {
            router.push(`/transaction/${txApproval.id}`)
          }
        },
        sign: async () => {
          const signApproval = approval as Approval<keyof ApprovalApi, Sign>
          approvalsStore.addNonStashedApproval(signApproval)

          if (
            signApproval.requestData.profileAddress === appStore.selectedProfile
          ) {
            approvalsStore.addProfileApproval(signApproval)
          } else {
            const data = await sendMessage({
              method: InternalMethods.SWITCH_PROFILE,
              payload: {
                address: signApproval.requestData.profileAddress,
              },
            })
            logInfo('streamSetup.processNotification sign', data)
            await appStore.init(data, appStore.lastPopupState, false)
            approvalsStore.setProfileApprovals(data.approvals)
          }

          if (!router.currentRoute.value.name?.toString().startsWith('/sign')) {
            router.push(`/sign/${signApproval.id}`)
          }
        },
        requestAccounts: async () => {
          const requestAccountsApproval = approval as Approval<
            keyof ApprovalApi,
            any
          >
          approvalsStore.addNonStashedApproval(requestAccountsApproval)
          if (
            !router.currentRoute.value.name?.toString().startsWith('/web3-auth')
          ) {
            router.push(`/web3-auth/${requestAccountsApproval.id}`)
          }
        },
        import: async () => {
          const importProfileApproval = approval as Approval<
            keyof ApprovalApi,
            any
          >
          approvalsStore.addNonStashedApproval(importProfileApproval)
          if (
            !router.currentRoute.value.name
              ?.toString()
              .startsWith('/qrcode/import-profile-dapp')
          ) {
            router.push(
              `/qrcode/import-profile-dapp/${importProfileApproval.id}`
            )
          }
        },
        relayer: async () => {
          const relayerApproval = approval as Approval<keyof ApprovalApi, any>
          approvalsStore.addNonStashedApproval(relayerApproval)
          if (
            !router.currentRoute.value.name
              ?.toString()
              .startsWith('/relayer-auth')
          ) {
            router.push(`/relayer-auth/${relayerApproval.id}`)
          }
        },
        switchNetwork: async () => {
          const switchApproval = approval as Approval<keyof ApprovalApi, any>
          approvalsStore.addNonStashedApproval(switchApproval)
          if (
            !router.currentRoute.value.name
              ?.toString()
              .startsWith('/network-switch')
          ) {
            router.push(`/network-switch/${switchApproval.id}`)
          }
        },
        addNetwork: async () => {
          const addNetworkApproval = approval as Approval<
            keyof ApprovalApi,
            any
          >
          approvalsStore.addNonStashedApproval(addNetworkApproval)
          if (
            !router.currentRoute.value.name
              ?.toString()
              .startsWith('/settings/network/')
          ) {
            router.push(`/settings/network/${addNetworkApproval.id}`)
          }
        },
        createAddress: async () => {
          //ignore the event if the extension is opened
        },
      }

      approvalHandlers[approval.type]()
    },
    [BGNotifications.ControllerUpdated]: async () => {
      await profileStore.fetchControllers()
    },
  }

  notificationAPI[data.method](data.payload)
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function initializeController(
  controllerStream: Duplex
): <T extends keyof InternalAPI>(parameters: {
  method: T
  payload: InternalAPI[T]['payload']
}) => Promise<InternalAPI[T]['response']> {
  if (!promiseManager) {
    promiseManager = new PromiseManager()
  }

  const processMessage = (
    data: any,
    _encoding: unknown,
    callback: (error?: Error | null) => void
  ) => {
    const doProcessMessage = async () => {
      if (data instanceof Error) {
        logError(`Error while processign stream messge in popup:`, data)
        return
      }
      if (!('id' in data)) {
        processNotification(data)
        return
      }
      if (data.error) {
        promiseManager.reject(data.id, data.error)
        return
      }

      promiseManager.resolve(data.id, data.result)
    }
    doProcessMessage()
      .catch(callback)
      .then(() => callback())
  }

  const stream = new Duplex({
    objectMode: true,
    read: () => false,
    write: processMessage,
  })

  pump(stream, controllerStream, stream)

  return <T extends keyof InternalAPI>(parameters: {
    method: T
    payload: InternalAPI[T]['payload']
  }): Promise<InternalAPI[T]['response']> => {
    const id = getUniqueId()
    stream.push({ ...parameters, id })
    return promiseManager.register(id)
  }
}

export const connect = () => {
  let isUIInitialised = false

  let port = browser.runtime.connect({ name: 'Popup' })
  let connectionStream = new PortDuplexStream(port)

  //inits stream connection to the background; can be re-called after reconnection
  const initConnection = () => {
    const mux = setupMultiplex(connectionStream)
    const sendMessage = initializeController(mux.createStream(CONTROLLER))
    const ethProvider = new EthProvider(mux.createStream(LOCAL_CHANNEL))

    useGlobals().init(ethProvider, sendMessage, router)

    // Reset timer one time
    logInfo('popup send keep alive (initial)')
    sendMessage({
      method: InternalMethods.KEEP_ALIVE,
      payload: undefined,
    })
  }

  //inits popup UI; should be just called once, after the connection is ready
  const initUI = () => {
    initConnection()
    setup()
    isUIInitialised = true
  }

  /*
   * Updates the stream after reconnection.
   * Waits on CONNECTION_READY message before rendering UI to prevent a white-page bug.
   * UI is rendered only after "CONNECTION_READY""
   */
  const messageListener = (message: {
    name: string
    data: { method: string }
  }) => {
    logInfo('stream message:', message)
    if (message?.name === 'CONNECTION_READY') {
      if (isUIInitialised) {
        initConnection()
      } else {
        initUI()
      }
    }
  }

  // resetExtensionStreamAndListeners takes care to remove listeners from closed streams
  // it also creates new streams and attach event listeners to them
  const resetExtensionStreamAndListeners = () => {
    logInfo('Port Disconnected', port.name)
    port.onMessage.removeListener(messageListener)
    port.onDisconnect.removeListener(resetExtensionStreamAndListeners)

    port = browser.runtime.connect({ name: 'Popup' })
    connectionStream = new PortDuplexStream(port)
    port.onMessage.addListener(messageListener)
    port.onDisconnect.addListener(resetExtensionStreamAndListeners)
  }

  port.onMessage.addListener(messageListener)
  port.onDisconnect.addListener(resetExtensionStreamAndListeners)
}
